import { Injectable, inject, NgZone } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { ICrossAppMessageData } from '@newroom-connect/library/interfaces';

import { LoggingService } from '../logging/logging.service';
import { ScriptService } from '../script/script.service';
import { CrossAppMessageService } from '../cross-app-communication/cross-app-message.service';

import { PixelStreamConnectionStatus, IVagonSessionInformation } from './pixel-stream.types';

@Injectable({
  providedIn: 'root'
})
export class PixelStreamService {
  private readonly loggingService = inject(LoggingService);
  private readonly scriptService = inject(ScriptService);
  private readonly ngZone = inject(NgZone);
  private readonly connectionStatusSubject = new BehaviorSubject<PixelStreamConnectionStatus>('disconnected');
  private readonly connectedSubject = new Subject<void>();
  private readonly disconnectedSubject = new Subject<void>();
  private readonly connectionErrorSubject = new Subject<Error>();
  private readonly streamMessageSubject = new Subject<ICrossAppMessageData>();
  private readonly sessionInformationSubject = new Subject<IVagonSessionInformation>();
  private isSdkLoaded = false;
  private hasEventListeners = false;

  public readonly connectionStatus$ = this.connectionStatusSubject.asObservable();
  public readonly connected$ = this.connectedSubject.asObservable();
  public readonly disconnected$ = this.disconnectedSubject.asObservable();
  public readonly connectionError$ = this.connectionErrorSubject.asObservable();
  public readonly streamMessage$ = this.streamMessageSubject.asObservable();
  public readonly sessionInformation$ = this.sessionInformationSubject.asObservable();

  /**
   *
   * @param sdkUrl
   *
   * @returns
   *
   * @throws
   */
  public async loadSDK(sdkUrl: string): Promise<void> {
    if (this.isSdkLoaded) {
      this.loggingService.debug('Vagon SDK already loaded');

      return;
    }

    try {
      this.loggingService.info('Loading Vagon SDK from:', sdkUrl);

      await this.scriptService.loadScript({ src: sdkUrl });

      this.isSdkLoaded = true;
      this.loggingService.info('Vagon SDK loaded successfully');
    } catch (error) {
      this.loggingService.error('Failed to load Vagon SDK', error);

      throw new Error('Failed to load Vagon SDK');
    }
  }

  /**
   *
   * @throws
   */
  public initializeEventListeners(): void {
    if (this.hasEventListeners) {
      this.loggingService.debug('Event listeners already attached');
      return;
    }

    if (!window.Vagon) {
      this.loggingService.error('Vagon SDK not available on window object');
      return;
    }

    try {
      // Connection events
      window.Vagon.onConnected(() => {
        this.loggingService.info('Pixel stream connected');

        this.updateConnectionStatus('connected');
        this.connectedSubject.next();
      });

      window.Vagon.onDisconnected(() => {
        this.loggingService.info('Pixel stream disconnected');

        this.updateConnectionStatus('disconnected');
        this.disconnectedSubject.next();
      });

      window.Vagon.onFailed(() => {
        this.loggingService.error('Pixel stream connection failed');

        this.updateConnectionStatus('error');
        this.connectionErrorSubject.next(new Error('Connection failed'));
      });

      // Application messages from Unreal/Unity
      window.Vagon.onApplicationMessage((event) => {
        try {
          const message = this.parseCrossAppMessageData(event.message);

          this.ngZone.run(() => {
            this.streamMessageSubject.next(message);
          });
        } catch (error) {
          this.loggingService.error('Failed to parse stream message', error);
        }
      });

      // Session information
      window.Vagon.onSessionInformation((data) => {
        this.loggingService.debug('Received session information:', data);

        this.sessionInformationSubject.next(data);
      });

      // Lifecycle events
      window.Vagon.onInitialization(() => {
        this.loggingService.debug('Stream initializing');

        this.updateConnectionStatus('connecting');
      });

      window.Vagon.onInstallationFailed(() => {
        this.loggingService.error('Stream installation failed');

        this.updateConnectionStatus('error');
        this.connectionErrorSubject.next(new Error('Installation failed'));
      });

      this.hasEventListeners = true;
      this.loggingService.info('Vagon event listeners initialized');
    } catch (error) {
      this.loggingService.error('Failed to initialize event listeners', error);
      throw error;
    }
  }

  /**
   * Parses and validates the data recieved from Pixel Stream CrossAppEvent.
   * @param rawMessage The raw Message recieved from Pixel Stream.
   * @returns Parsed Pixel Stream Message.
   * @throws ParseError when the event is not supported or the Pixel Stream Message is incomplete or missing data.
   */
  private parseCrossAppMessageData(rawMessage: unknown): ICrossAppMessageData {
    let messageData: ICrossAppMessageData | null = null;

    this.loggingService.debug('Received raw message from stream:', rawMessage);

    try {
      if (typeof rawMessage !== 'string') {
        throw new Error('Invalid raw message format. Expected a string.');
      }

      const parsedMessage = JSON.parse(rawMessage);

      // Extract and validate the message data so it fits the CrossAppMessageData interface.
      messageData = parsedMessage?.payload?.data;

      if (!CrossAppMessageService.isValidCrossAppMessageData(messageData)) {
        throw new Error('Invalid CrossAppMessage Data Format.');
      }
    } catch (error) {
      throw new Error(`ParseError: Failed to parse  message data. ${JSON.stringify(error, null, 2)}`);
    }

    this.loggingService.debug('Parsed message from stream:', messageData);

    return messageData;
  }

  /**
   *
   * @param status
   */
  public updateConnectionStatus(status: PixelStreamConnectionStatus): void {
    this.connectionStatusSubject.next(status);
  }

  /**
   *
   */
  public reset(): void {
    this.hasEventListeners = false;
    this.updateConnectionStatus('disconnected');
  }
}
